// ============================================================
// Tagged discriminated unions
// ============================================================
import { flowBrand, subflowBrand } from "./internals";

// A single primitive: `{ tag, data }`. Empty-shape tags carry `data: {}`. The
// rest of this module composes from it.

export type Tagged<K extends string, D> = { tag: K; data: D };

// Shape map `{ K1: D1; K2: D2; ... }` collapsed to a tagged union.
export type TaggedUnion<TShapes> = {
  [K in keyof TShapes & string]: Tagged<K, TShapes[K]>;
}[keyof TShapes & string];

// Strict: an empty-shape tag (`{}`) produces the zero-arg form; a shape
// with keys demands its data (and rejects excess properties).
type TaggedConstructor<K extends string, D> = [keyof D] extends [never]
  ? () => Tagged<K, D>
  : (data: D) => Tagged<K, D>;

export type TaggedConstructors<TShapes> = {
  [K in keyof TShapes & string]: TaggedConstructor<K, TShapes[K]>;
};

// Zero per-tag boilerplate: every property access returns a constructor
// that forwards its own key as `tag`.
//
// Why the `as T` cast is required:
// `new Proxy(target, handler)` is typed as the *target* ({}). The handler
// is what actually implements our interface at runtime — but TS can't
// introspect handler code, so it has no way to verify that every property
// access does what T promises. We assert the shape; the handler's `get`
// does exactly what the types claim (a constructor per string property).
//
// Why `data = {}` (not `undefined`):
// Empty-shape tags are typed as `Tagged<K, {}>` which mandates a non-nullish
// `data`. Defaulting to `{}` keeps the runtime value assignable to that type.
function makeTaggedProxy<T>(): T {
  return new Proxy(
    {},
    {
      get(_target, tag) {
        if (typeof tag !== "string") {
          return undefined;
        }
        return (data: unknown = {}) => ({ tag, data });
      },
    },
  ) as T;
}

export function tagged<TShapes>(): TaggedConstructors<TShapes> {
  return makeTaggedProxy<TaggedConstructors<TShapes>>();
}

// ============================================================
// Screen-flow DSL
// ============================================================
// A screen flow is a map of screens; each declares the data it needs
// and the outcomes its component can emit:
//
//   type Config = {
//     Welcome:    { outcomes: { continue: {} } };
//     BillUpload: { needs: { intent: Intent }; outcomes: { uploaded: { file: File } } };
//   };

// A dot is the path separator for a mounted child's screens (`Intake.First`),
// so no flow may name a screen with one.
export type ScreenKey<TConfig> = Exclude<
  keyof TConfig & string,
  `${string}.${string}`
>;

export type Needs<
  TConfig,
  TScreen extends keyof TConfig,
> = TConfig[TScreen] extends {
  needs: infer N;
}
  ? N
  : // `{}` is the "no needs" sentinel — consumed by
    // `[keyof Needs<…>] extends [never]` checks downstream.
    {};

export type Outcomes<
  TConfig,
  TScreen extends keyof TConfig,
> = TConfig[TScreen] extends { outcomes: infer O } ? O : never;

// A `Sub<F>` marker is the only screen config carrying a `flow` key.
type IsSub<TConfig, TScreen extends keyof TConfig> = TConfig[TScreen] extends {
  flow: unknown;
}
  ? true
  : false;

export type PlainScreenKey<TConfig> = {
  [K in ScreenKey<TConfig>]: IsSub<TConfig, K> extends true ? never : K;
}[ScreenKey<TConfig>];

// The currently-displayed screen plus the data required to render it.
export type FlowState<TConfig> = {
  [K in ScreenKey<TConfig>]: TConfig[K] extends { flow: infer F }
    ? Tagged<K, Needs<TConfig, K>> & {
        sub: {
          state: FlowState<ConfigOf<F>>;
          bindOnDone: BindOnDone<ConfigOf<F>>;
        };
      }
    : Tagged<K, Needs<TConfig, K>>;
}[ScreenKey<TConfig>];

// Inheriting by name alone would let a same-named field of an incompatible
// type silently stand in for the target's. `Pick` rather than `TFrom[P]` so
// the comparison carries the `?` modifier too: an optional source field
// doesn't satisfy a required target field, even when the value types match.
type InheritedKeys<TFrom, TTo> = {
  [P in keyof TFrom & keyof TTo]: [Pick<TFrom, P>] extends [Pick<TTo, P>]
    ? P
    : never;
}[keyof TFrom & keyof TTo];

// The *new* fields the target needs that the source state doesn't already
// carry. Drives the "only supply what's new" transition API — the hook
// accumulates the rest. Keys the source doesn't have at all come from the
// first half; a shared key it carries at a type the target won't accept lands
// in the second half as required (`-?`), so the transition has to overwrite it
// rather than let the incompatible value ride through the merge.
type ForwardPatch<TFrom, TTo> = Omit<TTo, keyof TFrom> & {
  [P in Exclude<keyof TFrom & keyof TTo, InheritedKeys<TFrom, TTo>>]-?: TTo[P];
};

// A forward transition. Empty-needs targets carry `data: {}`; targets with
// declared needs carry the forward patch (the fields the source doesn't
// already have). An empty forward patch (`{}`) is legal — it's the shape
// when the source already satisfies every field of the target.
type GoToAction<TConfig, TFrom extends keyof TConfig> = {
  [TTo in ScreenKey<TConfig>]: [keyof Needs<TConfig, TTo>] extends [never]
    ? { tag: TTo; data: {} }
    : {
        tag: TTo;
        data: IsSub<TConfig, TTo> extends true
          ? Needs<TConfig, TTo>
          : ForwardPatch<Needs<TConfig, TFrom>, Needs<TConfig, TTo>>;
      };
}[ScreenKey<TConfig>];

// Structurally distinct from forward actions (`type` vs `tag`) so it never
// collides with a screen named "back".
type BackAction = { type: "back" };

// Returns to a screen already visited without discarding later visits. A new
// transition from the earlier cursor replaces that retained future branch.
type RewindAction<TConfig> = { type: "rewind"; tag: PlainScreenKey<TConfig> };

export type FlowResult<TConfig, TFrom extends keyof TConfig> =
  | GoToAction<TConfig, TFrom>
  | BackAction
  | RewindAction<TConfig>;

// Tagged constructor for `createFlow`. A screen with keys keeps a zero-arg
// overload, but it returns `Tagged<K, {}>` — matching the runtime value the
// proxy produces (`{ data: {} }`), not `Tagged<K, D>`. So `Screen.X()` is
// assignable to a handler's `FlowResult` only when the target's forward patch is
// empty or all-optional — i.e. the source already carries every required field.
// A transition that *introduces* a new required field the source doesn't carry
// must use the data overload; the zero-arg form is a compile error there.
//
// Returning `Tagged<K, D>` would be unsound: since `D ⊇ ForwardPatch`, the
// "claims-full-D" zero-arg satisfies *any* patch, silently skipping required
// data the source never carried.
//
// The data overload takes a `Partial<D>`-shaped literal to support partial
// forward patches (source already carries some fields), intersected with a
// per-key guard pinning every supplied key to `D[P]`. `Partial<D>` alone would
// admit `undefined` for a field the target declares required; the conditional
// also maps excess keys to `never` so they are rejected.
//
// `NoInfer` keeps a contextual `FlowResult` / `FlowState` return type from
// participating in `TActual` inference. The argument remains the sole source
// of its literal keys and values instead of being widened toward `Partial<D>`.

type FlowShapeConstructor<K extends string, D> = [keyof D] extends [never]
  ? () => Tagged<K, {}>
  : {
      (): Tagged<K, {}>;
      <TActual extends Partial<D>>(
        data: TActual & {
          [P in keyof TActual]: P extends keyof D ? D[P] : never;
        },
      ): Tagged<K, NoInfer<TActual>>;
    };

type FlowConstructors<TConfig> = {
  [K in ScreenKey<TConfig>]: FlowShapeConstructor<K, Needs<TConfig, K>>;
};

export function createFlow<TConfig>(): FlowConstructors<TConfig> {
  return makeTaggedProxy<FlowConstructors<TConfig>>();
}

export const back = (): BackAction => ({ type: "back" });

/**
 * Reopen the most recent earlier visit to `tag`. Back lands on whatever preceded
 * it, Forward restores later visits, and a new outcome replaces that future branch.
 * A no-op when the screen isn't in history. A mount has no visit of its own, so
 * `tag` names a plain screen.
 */
export const rewindTo = <TConfig>(
  tag: PlainScreenKey<TConfig>,
): RewindAction<TConfig> => ({ type: "rewind", tag });

// The hook intercepts `done`: the reducer never sees it, so the flow stays on
// the screen that exited.
export type DoneAction<TResult> = { type: "done"; result: TResult };

// `TaggedUnion<T>` is a mapped-type lookup, so TS can't infer the shape map
// back out of an argument — hence the result value type.
export const done = <TResult extends Tagged<string, unknown>>(
  result: TResult,
): DoneAction<TResult> => ({ type: "done", result });

type HandlerResult<TConfig, TFrom extends keyof TConfig, TDone> = [
  TDone,
] extends [never]
  ? FlowResult<TConfig, TFrom>
  : FlowResult<TConfig, TFrom> | DoneAction<TaggedUnion<TDone>>;

// ============================================================
// Transition table
// ============================================================
// Exhaustive: every screen and every outcome must be handled.

export type TransitionTable<TConfig, TCtx = void, TDone = never> = {
  [K in ScreenKey<TConfig>]: TConfig[K] extends { flow: infer F }
    ? SubflowEntry<F, TConfig, K, TCtx, TDone>
    : {
        [O in keyof Outcomes<TConfig, K> & string]: (
          output: Outcomes<TConfig, K>[O],
          ctx: TCtx,
          // The source screen's committed state, so a handler can branch on
          // what it already carries.
          current: Tagged<K, Needs<TConfig, K>>,
        ) => HandlerResult<TConfig, K, TDone>;
      };
};

export type Flow<TConfig, TStart = {}, TDone = never, TCtx = void> = {
  initial: (start: TStart) => FlowState<TConfig>;
  transitions: TransitionTable<TConfig, TCtx, TDone>;
};

// A host cannot recover these type parameters from a plain `Flow` because
// they appear only inside mapped types. `defineFlow` pins them in required,
// branded metadata. Arrays create that metadata without a type assertion.
type FlowTypes<TConfig, TStart, TDone, TCtx> = {
  config: TConfig[];
  start: TStart[];
  done: TDone[];
  ctx: TCtx[];
};

export type DefinedFlow<TConfig, TStart, TDone, TCtx> = Flow<
  TConfig,
  TStart,
  TDone,
  TCtx
> & { readonly [flowBrand]: FlowTypes<TConfig, TStart, TDone, TCtx> };

/** Any flow authored with `defineFlow` — the only thing a host can mount. */
export type MountableFlow = {
  readonly [flowBrand]: FlowTypes<unknown, unknown, unknown, unknown>;
};

export type ConfigOf<F> = F extends {
  [flowBrand]: { config: (infer C)[] };
}
  ? C
  : never;
type StartOf<F> = F extends { [flowBrand]: { start: (infer S)[] } } ? S : never;
type DoneOf<F> = F extends { [flowBrand]: { done: (infer D)[] } } ? D : never;
type CtxOf<F> = F extends { [flowBrand]: { ctx: (infer C)[] } } ? C : never;

// A typed identity: it only pins the generics so `typeof flow` carries them.
export function defineFlow<
  TConfig,
  TStart = {},
  TDone = never,
  TCtx = void,
>(): (
  flow: Flow<TConfig, TStart, TDone, TCtx>,
) => DefinedFlow<TConfig, TStart, TDone, TCtx> {
  return flow => ({
    ...flow,
    [flowBrand]: { config: [], start: [], done: [], ctx: [] },
  });
}

/** Config marker. `needs` are the child's start inputs; `outcomes` are its done shapes. */
export type Sub<F extends MountableFlow> = {
  needs: StartOf<F>;
  outcomes: DoneOf<F>;
  flow: F;
};

// The host's ctx is what the child's handlers receive, so it has to satisfy
// the child's. A child that declares none mounts anywhere.
// biome-ignore lint/suspicious/noConfusingVoidType: a flow with no ctx declares `void`, which `undefined` does not match
type MountableIn<F, TCtx> = [CtxOf<F>] extends [void]
  ? F
  : [TCtx] extends [CtxOf<F>]
    ? F
    : { "the host's ctx does not satisfy the child's": CtxOf<F> };

export type SubflowEntry<F, THost, K extends ScreenKey<THost>, TCtx, TDone> = {
  // Runtime brand avoids mistaking plain outcomes named `flow` and `onDone`
  // for a mounted child.
  readonly [subflowBrand]: true;
  flow: F & MountableIn<F, TCtx>;
  onDone: {
    [O in keyof DoneOf<F> & string]: (
      result: DoneOf<F>[O],
      ctx: TCtx,
      current: Tagged<K, Needs<THost, K>>,
    ) => HandlerResult<THost, K, TDone>;
  };
};

// `THost`, `K`, `TCtx` and `TDone` come from the contextual type at the
// table position, which is what gives the handlers their parameter types.
export function subflow<
  F extends MountableFlow,
  THost,
  K extends ScreenKey<THost>,
  TCtx,
  TDone,
>(
  flow: F & MountableIn<F, TCtx>,
  onDone: SubflowEntry<F, THost, K, TCtx, TDone>["onDone"],
): SubflowEntry<F, THost, K, TCtx, TDone> {
  return { [subflowBrand]: true, flow, onDone };
}

// ============================================================
// Component contract
// ============================================================
// Screens call `onDone(outcome)` with a tagged outcome built via
// `tagged<Outcomes>()`. Each screen owns its own outcome shape map and
// types its prop as `OnDone<Outcomes>` — independent of the surrounding
// config. The hook's `bindOnDone(tag)` returns the same shape, so
// assignment type-checks structurally.
export type OnDone<TShapes> = (outcome: TaggedUnion<TShapes>) => void;

export type BindOnDone<TConfig> = <K extends PlainScreenKey<TConfig>>(
  tag: K,
) => OnDone<Outcomes<TConfig, K>>;

export {
  type FlowTimelineFrame,
  type FlowTimelineTransitionGroup,
  type FlowTimelineTransitionProps,
  groupFlowTimelineFrames,
} from "./timelineTransition";
export { useBrowserHistorySync } from "./useBrowserHistorySync";
export { useBrowserTimelineSync } from "./useBrowserTimelineSync";
export {
  type FlowTransition,
  type FlowVisit,
  type RawFlowFrame,
  useNavigableFlow,
  visitKeyAt,
} from "./useNavigableFlow";
